"use client";

import { cn } from "@newxsystems/shared";
import { useCallback, useEffect, useState, type ComponentType } from "react";
import type { EffectfulHeroAnimation } from "./EffectfulHeroBlock.props";
import { WORKER_ANIMATIONS } from "../../../../effects/EffectFx";
import { EffectFxHost } from "../../../../effects/EffectFxHost.client";
import { FX_OPACITY, FX_SURFACE } from "../../../../effects/EffectFxSurface";

/**
 * One lazy loader per background style. The block's "Background animation" field picks
 * the entry, so only that style's chunk is fetched; every file exports a default.
 * `aero-shards` and `shape-waves` stay React components (WebGPU cannot run in workers
 * on Firefox/Safari); the other styles only use these components as the fallback for
 * browsers without OffscreenCanvas — the default path runs them in a worker.
 */
const ANIMATIONS: Record<EffectfulHeroAnimation, () => Promise<{ default: ComponentType }>> = {
  aurora: () => import("./EffectfulHeroAurora.client"),
  waves: () => import("./EffectfulHeroWaves.client"),
  "dot-field": () => import("./EffectfulHeroDotField.client"),
  dither: () => import("./EffectfulHeroDither.client"),
  beams: () => import("./EffectfulHeroBeams.client"),
  "gradient-blinds": () => import("./EffectfulHeroGradientBlinds.client"),
  "line-waves": () => import("./EffectfulHeroLineWaves.client"),
  prism: () => import("./EffectfulHeroPrism.client"),
  lightfall: () => import("./EffectfulHeroLightfall.client"),
  "aero-shards": () => import("./EffectfulHeroAeroShards.client"),
  "shape-waves": () => import("./EffectfulHeroShapeWaves.client"),
  "pattern-waves": () => import("./EffectfulHeroPatternWaves.client"),
  "gradient-waves": () => import("./EffectfulHeroGradientWaves.client"),
};

const WORKER_SET: ReadonlySet<EffectfulHeroAnimation> = new Set(WORKER_ANIMATIONS);

export const EffectfulHeroEffectStage = ({
  animation,
  className,
}: { animation?: EffectfulHeroAnimation | null; className?: string } = {}) => {
  const style = animation ?? "aurora";
  const [EffectComponent, setEffectComponent] = useState<ComponentType | null>(null);
  const [worker, setWorker] = useState(false);
  const [live, setLive] = useState(false);

  const handleWorkerFailed = useCallback(() => setWorker(false), []);
  // The fade needs a settled frame behind it; one rAF defers past the worker's first draw.
  const handleWorkerReady = useCallback(() => {
    requestAnimationFrame(() => setLive(true));
  }, []);

  useEffect(() => {
    const workerCapable = WORKER_SET.has(style);

    // Fade the effect out while the next style loads, so switching never flashes the
    // previous canvas behind the new one.
    setLive(false);
    setEffectComponent(null);
    setWorker(workerCapable);

    if (workerCapable) return undefined;

    let cancelled = false;
    const load = ANIMATIONS[style];
    void load()
      .then((module) => {
        if (!cancelled) setEffectComponent(() => module.default);
      })
      .catch(() => {
        // The animation is optional: the block's image stays as the background.
      });
    return () => {
      cancelled = true;
    };
  }, [style]);

  // Legacy mode only: two frames after the component mounts before the fade, so the
  // first effect frames land before the poster is transparent.
  useEffect(() => {
    if (worker || !EffectComponent) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setLive(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [worker, EffectComponent]);

  const surface = FX_SURFACE[style];

  return (
    // The canvas is not hit-testable while `aria-hidden` overlays sit above it, so the
    // stage is only as opaque as the hardcoded per-style map allows.
    <div
      aria-hidden="true"
      className={cn("absolute inset-0", className)}
      style={{ opacity: FX_OPACITY[style] ?? 1 }}
    >
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none",
          live ? "opacity-100" : "opacity-0",
        )}
      >
        {worker ? (
          <EffectFxHost
            animation={style}
            containerClass={surface?.containerClass}
            containerStyle={surface?.containerStyle}
            onFailed={handleWorkerFailed}
            onReady={handleWorkerReady}
            worker
          />
        ) : EffectComponent ? (
          <EffectComponent />
        ) : null}
      </div>
    </div>
  );
};

EffectfulHeroEffectStage.displayName = "EffectfulHeroBlock::EffectfulHeroEffectStage";
